import React, { useEffect } from 'react';
import { Redirect, Link, useLocation } from 'react-router-dom';
import { useLoggedInUser } from '../../../resources/user/authService';
import DefaultLayout from '../layouts/DefaultLayout';
import { useMyOrgsAndGroups } from '../../../resources/organization/organizationService';
import { formatDate } from '../../utils/displayUtils';
import Breadcrumbs from '../navigation/Breadcrumbs';
import Spinner from '../helpers/Spinner';


const Landing = () => {
  const [redirectTo, setRedirectTo] = React.useState(null);
  const location = useLocation();
  const loggedInUser = useLoggedInUser();
  // Fetch orgs for user and list them, if only one, redirect to that org's dashboard (eg: `root/:organizationId)
  const { organizations, groupUsers, isLoading } = useMyOrgsAndGroups({ organizationId: null });

  const groupUsersByOrg = React.useMemo(() => {
    const byOrg = new Map();
    (groupUsers || []).forEach((gu) => {
      const orgId = gu?._organization;
      if (!orgId) return;
      const arr = byOrg.get(orgId);
      if (arr) arr.push(gu);
      else byOrg.set(orgId, [gu]);
    });
    return byOrg;
  }, [groupUsers]);

  useEffect(() => {
    // Only redirect to login when we are sure the user is unauthenticated (null)
    if (loggedInUser === null) {
      setRedirectTo('/user/login');
      return;
    }
    if (loggedInUser && organizations?.length === 1) {
      setRedirectTo(`/${organizations[0]._id}`);
    }
  }, [loggedInUser, organizations]);

  if (redirectTo) {
    return <Redirect to={redirectTo} />
  }

  const welcomeMessage = `Welcome ${loggedInUser?.firstName ? `, ${loggedInUser.firstName}` : ''}!`;

  if (isLoading) {
    return (
      <DefaultLayout className='h-screen flex items-center justify-center'>
        <Spinner />
      </DefaultLayout>
    );
  }

  if (organizations?.length > 1) {
    return (
      <DefaultLayout
        className={'pb-2 bg-neutral-sec min-h-full'}
      >
        <Breadcrumbs links={location.state?.breadcrumbs || []} />
        <h1 className="text-neutral-ter text-2xl mt-0 mb-4">
          <i className="fa-regular fa-house fa-sm my-auto mr-2 text-neutral-sec"></i>
          {welcomeMessage}
        </h1>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {organizations.map((org) => {
            const orgGroupUsers = groupUsersByOrg.get(org?._id) || [];
            const oldestGroupUser = orgGroupUsers
              .slice()
              .sort((a, b) => {
                const da = a?.created ? new Date(a.created).getTime() : Infinity;
                const db = b?.created ? new Date(b.created).getTime() : Infinity;
                return da - db;
              })[0];
            return (
              <Link
                key={org?._id}
                to={`/${org?._id}`}
                className="flex flex-row items-center bg-neutral-pri hover:shadow-lg rounded-lg p-6 gap-2 transition-transform border border-neutral-pri min-h-[100px] w-full max-w-[800px] cursor-pointer group hover:no-underline hover:border-brand-accent-pri focus-visible:outline-none focus-visible:ring-2"
                title={`go to ${org?.name} organization`}
                tabIndex={1}
              >
                <div className="flex flex-col flex-1 gap-2">
                  <h2 className="text-lg m-0 font-semibold text-brand-pri group-hover:!text-brand-accent-pri truncate">
                    {org?.name}
                  </h2>
                  <p className="text-sm text-neutral-ter">
                    Joined: {oldestGroupUser?.created ? formatDate(oldestGroupUser.created) : 'N/A'}
                  </p>
                  <p className='text-sm text-neutral-ter'>
                    {orgGroupUsers.length} {orgGroupUsers.length === 1 ? 'Workspace' : 'Workspaces'}
                  </p>
                </div>
                <div className="flex items-center justify-center flex-shrink-0 w-auto h-10">
                  <OrgLogo name={org?.name} logo={org?.logo} />
                </div>
              </Link>
            );
          })}
        </div>
      </DefaultLayout>
    );
  }

  // Empty state for users with no organizations
  if (!isLoading && (organizations?.length ?? 0) === 0) {
    return (
      <DefaultLayout className='h-screen'>
        <h1 className="text-neutral-ter mt-0 mb-4">{welcomeMessage}</h1>
        <div className="rounded-lg border border-neutral-pri bg-neutral-pri p-6 max-w-screen-md flex flex-col gap-8">
          <p>
            You don’t currently have access to any organizations. Access to workspaces is invite-only.
          </p>
          <p>
            If you previously had access, it may have been removed or your invitation may have expired. Please contact your organization admin to <strong>request a new invite</strong>.
          </p>
          <p>
            If you need further assistance, please reach out to our support team at <a href="mailto:prosupport@simulations-plus.com" className='cursor-pointer'>prosupport@simulations-plus.com</a>.
          </p>
        </div>
      </DefaultLayout>
    );
  }

  return null;
}

export default Landing;



// logo component with a proper fallback
const OrgLogo = ({ name, logo }) => {
  const [errored, setErrored] = React.useState(false);
  const initial = name?.[0]?.toUpperCase() || '?';
  if (!logo || errored) {
    return (
      <div
        className="flex items-center justify-center w-12 h-12 bg-neutral-100 rounded-full text-neutral-400 text-2xl font-bold"
        aria-hidden="true"
      >
        {initial}
      </div>
    );
  }
  return (
    <img
      src={logo}
      alt={`${name} logo`}
      className="h-10 w-20 object-contain transition-opacity bg-neutral-50 group-hover:opacity-90 group-focus:opacity-90"
      onError={() => setErrored(true)}
    />
  );
};